/**
 * NewsLegend — Compact "?" help chip explaining EventBadge severity tiers.
 * Click / hover to reveal a popover with the full legend.
 */
import React from 'react';
import { AlertCircle, AlertTriangle, HelpCircle } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

const Row = ({ swatch, label, desc }) => (
  <div className="flex items-start gap-2 text-[11px]">
    <span className="shrink-0 mt-0.5">{swatch}</span>
    <div className="min-w-0">
      <div className="font-bold text-foreground">{label}</div>
      <div className="text-[10px] text-muted-foreground leading-snug">{desc}</div>
    </div>
  </div>
);

const NewsLegend = ({ className = '' }) => (
  <div className={`flex items-center ${className}`} data-testid="news-legend-wrapper">
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors cursor-help px-2 py-1 border border-border/50 rounded-sm"
          data-testid="news-legend-trigger"
          aria-label="Show news badge legend"
        >
          <HelpCircle className="w-3 h-3" />
          News legend
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-80 p-3 space-y-2"
        data-testid="news-legend-popover"
      >
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold border-b border-border pb-2 mb-2">
          How to read the news badges
        </div>

        <Row
          swatch={
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 border rounded-sm text-[9px] font-bold font-mono text-red-400 bg-red-500/10 border-red-500/40">
              <AlertCircle className="w-3 h-3" />N
            </span>
          }
          label="RED — blocks new picks"
          desc="Material adverse events: SEBI orders, rating downgrades, CEO/CFO/MD resignations, insolvency, audit qualifications, default events."
        />

        <Row
          swatch={
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 border rounded-sm text-[9px] font-bold font-mono text-amber-400 bg-amber-500/10 border-amber-500/40">
              <AlertTriangle className="w-3 h-3" />N
            </span>
          }
          label="WATCH — flag only"
          desc="Worth a closer look but not auto-excluded: director changes, promoter sales, pledge creation, earnings within 3 days, profit warnings."
        />

        <Row
          swatch={
            <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-muted-foreground/60 mt-1" />
          }
          label="INFO — routine"
          desc="Board meetings, press releases, analyst meet intimations, investor day updates. Independent-director rotations also counted here."
        />

        <div className="pt-2 border-t border-border text-[10px] text-muted-foreground italic leading-snug">
          Hover any badge for the full filing title + date. Window = last 7 days of NSE corporate announcements.
        </div>
      </PopoverContent>
    </Popover>
  </div>
);

export default NewsLegend;
